import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { string } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ExpirationTimer, useCartContext, useLeisurePurchaseContext } from 'components';
import { formatLocalizedCurrency } from 'helpers';
import { useFetch, useUser } from 'hooks';

const LeisurePurchaseSummary = ({ className = '' }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { cart } = useCartContext();
  const { currentUser } = useUser();
  const { currentStep } = useLeisurePurchaseContext();

  const shippingPrice = useSelector((state) => state.leisurePurchase.shippingPrice);
  const subventionAmount = useSelector((state) => state.leisurePurchase.subvention ?? 0);
  const storedDepositAmount = useSelector((state) => state.leisurePurchase.depositAmount);

  const { call: orderCartCall, data: orderCart } = useFetch();
  const orderId = searchParams.get('orderId');

  // Determine the source of the cart (cart or orderCart if cart have been paid)
  const cartContent = useMemo(() => (!isEmpty(cart?.lines) ? cart : orderCart), [cart, orderCart]);

  // Total price calculation for cart
  const totalPrice = useMemo(() => {
    let price = cartContent?.priceWithoutShipping / 100 || 0;
    if (cartContent?.discountAmount > 0) price = price - cartContent.discountAmount / 100;
    return price - subventionAmount / 100 + shippingPrice / 100;
  }, [cartContent, shippingPrice, subventionAmount]);

  useEffect(() => {
    if (isEmpty(cart?.lines)) {
      getOrderCart();
    }
  }, [cart?.lines]);

  const getOrderCart = async () => {
    if (!!orderId && isEmpty(currentUser)) return;
    await orderCartCall({
      service: 'checkout',
      path: `/${currentUser.serialNumber}/order/cart/${orderId}`,
      method: 'get'
    });
  };

  // Check if cart and orderCart are both empty, return null if they are
  if (isEmpty(cart?.lines) && isEmpty(orderCart?.lines)) return null;
  return (
    <div className={clsx('h-auto rounded-xl bg-white mb-4 shadow-card lg:border lg:border-gray-200', className)}>
      <div className="hidden lg:block lg:p-4">
        {/* TIMER */}
        <div className="flex-center-between mb-4">
          <p className="font-bold">{t('cart:cart')}</p>
          <ExpirationTimer />
        </div>

        {/* LEISURE PRODUCTS */}
        <div className="grid grid-cols-12 items-center gap-2 p-2 bg-gray-100 rounded-lg">
          <p className="col-span-7 uppercase">{t('cart:label')}</p>
          <p className="col-span-3 uppercase text-right">{t('cart:price')}</p>
          <p className="col-span-2 uppercase text-right">{t('quantity')}</p>
        </div>

        <div className="lg:max-h-[18.75rem] lg:overflow-auto">
          {cartContent?.lines?.map((product, index) => (
            <div
              key={`leisure-product-${index}`}
              /* eslint-disable-next-line */
              className="grid grid-cols-12 items-center gap-2 py-4 border-b border-gray-200 last:border-none last:pb-0"
              /* eslint-enable */
            >
              <div className="col-span-7 truncate-line four-lines">{product.label}</div>
              <div className="col-span-3 text-right text-secondary-500">
                {formatLocalizedCurrency(product.unitPrice / 100)}
              </div>
              <div className="col-span-2 text-center">{product.quantity}</div>
            </div>
          ))}
        </div>
      </div>

      {/* PRICES */}
      <div className="prices-container">
        {/* LEISURE TOTAL PRICE */}
        <div className="flex justify-between mb-2 md:mb-4">
          <p>{t('purchase:totalPriceTTC')}</p>
          <p className="font-bold">{formatLocalizedCurrency(cartContent?.priceWithoutShipping / 100)}</p>
        </div>

        {/* SHIPPING PRICE */}
        {shippingPrice > 0 && (
          <div className="flex justify-between mb-2 md:mb-4">
            <p>{t('purchase:shippingPrice')}</p>
            <p className="font-bold">{formatLocalizedCurrency(shippingPrice / 100)}</p>
          </div>
        )}

        {/* DISCOUNT AMOUNT */}
        {cartContent?.discountAmount > 0 && (
          <div className="flex justify-between mb-2 md:mb-4">
            <p>{t('products:employerPrice')} </p>
            <p className="font-bold">-{formatLocalizedCurrency(cartContent?.discountAmount / 100)}</p>
          </div>
        )}

        {/* SUBVENTION AMOUNT */}
        {subventionAmount > 0 && (
          <div className="flex justify-between mb-2 md:mb-4">
            <p>{t('purchase:subvention.title')} </p>
            <p className="font-bold">-{formatLocalizedCurrency(subventionAmount / 100)}</p>
          </div>
        )}

        {/* TOTAL PRICE TO PAY */}
        <div className={clsx('flex justify-between', storedDepositAmount && 'mb-2 md:mb-4')}>
          <p>{t('purchase:totalToPay')}</p>
          <p className="font-bold">{formatLocalizedCurrency(totalPrice)}</p>
        </div>

        {/* DEPOSIT */}
        {currentStep.order > 2 && storedDepositAmount && (
          <div className="flex justify-between">
            <p>{t('purchase:deposit')} </p>
            <p className="font-bold">{formatLocalizedCurrency(storedDepositAmount)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

LeisurePurchaseSummary.propTypes = {
  className: string
};

export default LeisurePurchaseSummary;
